<template>
    <div class="acceptance-configuration-selector">
        <b-form-group :label="label">
            <div class="d-flex mb-2">
                <SearchComponent
                    v-model="selectedConfig"
                    label=""
                    search-u-r-l="/AcceptanceConfiguration"
                    :disabled="disabled"
                    :prepopulate="true"
                    :results-modifier="filterActiveConfigs"
                    class="flex-fill mb-0"
                    placeholder="Search acceptance configurations..."
                    :option-label="getConfigLabel"
                    @input="addConfiguration"
                />
            </div>

            <div v-if="value && value.length > 0" class="selected-configurations">
                <b-list-group>
                    <b-list-group-item
                        v-for="config in value"
                        :key="config.id"
                        class="d-flex justify-content-between align-items-center py-2"
                    >
                        <div>
                            <strong>{{ config.name }}</strong>
                            <b-badge v-if="config.requiresSignature" variant="warning" class="ml-2">
                                Requires Signature
                            </b-badge>
                            <b-badge v-if="config.mandatory" variant="info" class="ml-1">
                                Mandatory
                            </b-badge>
                        </div>
                        <b-button
                            v-if="!disabled"
                            variant="danger"
                            size="sm"
                            @click="removeConfiguration(config.id)"
                        >
                            <i class="fa fa-times" />
                        </b-button>
                    </b-list-group-item>
                </b-list-group>
            </div>
            <div v-else class="text-muted">
                <small>No acceptance configurations selected. Search above to add configurations.</small>
            </div>
        </b-form-group>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { AcceptanceConfiguration, AcceptanceConfigurationStatus } from '@/models/AcceptanceConfiguration';
import SearchComponent from '@/components/SearchComponent.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    components: {
        SearchComponent
    }
})
export default class AcceptanceConfigurationSelector extends Vue {
    @Prop({ default: () => [] }) value!: AcceptanceConfiguration[];
    @Prop({ default: 'Acceptance Configurations' }) label!: string;
    @Prop({ default: false }) disabled!: boolean;

    selectedConfig: AcceptanceConfiguration | null = null;

    /**
     * The endpoint returns every status. Left unfiltered, opening the dropdown would lead with
     * Draft and Inactive configurations - which only became visible once the list stopped
     * requiring a keystroke. Matches AcceptanceConfigSelector, which filters the same endpoint.
     * Configurations already selected render from `value`, so an existing pick is never dropped.
     */
    filterActiveConfigs(configs: AcceptanceConfiguration[]): AcceptanceConfiguration[] {
        return configs.filter(c => c.status === AcceptanceConfigurationStatus.Active);
    }

    getConfigLabel(config: AcceptanceConfiguration): string {
        if (!config) return '';
        return `${config.id} - ${config.name}`;
    }

    addConfiguration(config: AcceptanceConfiguration | null) {
        if (!config || !config.id) return;

        // Check if already added
        if (this.value.some(c => c.id === config.id)) {
            this.$notification(NotificationOptions.warning('This configuration has already been added'));
            this.selectedConfig = null;
            return;
        }

        const updatedConfigs = [...this.value, config];
        this.$emit('input', updatedConfigs);
        this.selectedConfig = null;
    }

    removeConfiguration(configId: number) {
        const updatedConfigs = this.value.filter(c => c.id !== configId);
        this.$emit('input', updatedConfigs);
    }
}
</script>

<style scoped>
.acceptance-configuration-selector .selected-configurations {
    max-height: 200px;
    overflow-y: auto;
}

.acceptance-configuration-selector .list-group-item {
    padding: 0.5rem 0.75rem;
}
</style>
